import { useRef } from "react";
import { useInView } from "framer-motion";
import { useIsMobile } from "@/hooks/useIsMobile";

// Ambient light for dark (ink) sections — one or two very soft accent blurs
// that drift almost imperceptibly on a slow loop. Adds depth and a
// "futuristic minimal" feel to otherwise flat blocks of copy without
// parallax, particles, or anything that competes with the text. Palette
// stays exactly on-brand: accent color only, low opacity.
//
// Perf note: the drift itself is a plain CSS `@keyframes` animation (see
// .animate-glow-drift in index.css) — it runs on the compositor thread
// instead of framer-motion's per-frame JS interpolation. React's only job
// is toggling the class on/off: this component is mounted up to ~9 times
// on the Home page, and with every instance's loop live on the main
// thread it was heavy enough that even the hero video couldn't finish
// loading its metadata. Still gated the same way as before — animates
// only while its section is in (or near) the viewport, off entirely on
// mobile, where the reduced blur radius (150px is heavy for a phone GPU
// either way) also drops via the `lg:` breakpoint.
export function AmbientGlow({ variant = "single", tone = "accent", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "200px 0px" });
  const isMobile = useIsMobile();
  const toneClass = tone === "soft" ? "bg-accentSoft/10" : "bg-accent/10";
  const shouldAnimate = inView && !isMobile;

  return (
    <div ref={ref} className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      <div
        className={`absolute w-[55vw] h-[55vw] max-w-[640px] max-h-[640px] rounded-full ${toneClass} blur-3xl lg:blur-[150px] ${
          shouldAnimate ? "animate-glow-drift" : ""
        }`}
        style={{ top: "-15%", left: "-12%", "--drift-x": "36px", "--drift-y": "22px", "--drift-duration": "24s" }}
      />
      {variant === "double" && (
        <div
          className={`absolute w-[42vw] h-[42vw] max-w-[520px] max-h-[520px] rounded-full ${toneClass} blur-3xl lg:blur-[150px] ${
            shouldAnimate ? "animate-glow-drift" : ""
          }`}
          style={{
            bottom: "-18%",
            right: "-10%",
            "--drift-x": "-28px",
            "--drift-y": "-18px",
            "--drift-duration": "28s",
            "--drift-delay": "2s",
          }}
        />
      )}
    </div>
  );
}
